import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loading from 'react-loading';
import { Form } from 'semantic-ui-react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import { useMutation } from '@apollo/react-hooks';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Main, Button, Send } from './styles';
import { MdSend } from 'react-icons/md';

import {
  ADD_SERVICE_CONFIG,
  UPDATE_SERVICE_CONFIG
} from '../../../services/graphql/mutations';

import {
  schema,
  formInitialState,
  formErrorInitialState,
  timeMask
} from './constants';

export default function ServicesConfigurationModal({
  edit,
  onClose,
  selectedValue,
  open,
  selected = {}
}) {
  const [
    {
      exec_seconds,
      exec_time,
      name,
      description,
      login,
      password,
      ipv4_address,
      one_time_day,
      active,
      port
    },
    setFormState
  ] = useState(formInitialState);
  const [
    {
      exec_secondsError,
      exec_timeError,
      nameError,
      descriptionError,
      loginError,
      passwordError,
      ipv4_addressError,
      portError
    },
    setFormErrorState
  ] = useState(formErrorInitialState);

  const [addSupplier, { loading: addLoading }] = useMutation(
    ADD_SERVICE_CONFIG
  );
  const [updateSupplierConfig, { loading: updateLoading }] = useMutation(
    UPDATE_SERVICE_CONFIG
  );

  useEffect(() => {
    if (open) {
      setFormState(formInitialState);
      setFormErrorState(formErrorInitialState);
    }

    if (edit && open) {
      const {
        exec_seconds,
        exec_time,
        name,
        description,
        login,
        password,
        ipv4_address,
        one_time_day,
        active,
        port
      } = selected;

      setFormState({
        exec_seconds,
        exec_time,
        name,
        description,
        login,
        password,
        ipv4_address,
        one_time_day,
        active,
        port
      });
    }
  }, [open, edit, selected]);

  function handleOnChange(name, value) {
    setFormState(prevState => ({ ...prevState, [name]: value }));
  }

  function validateDates() {
    let isValid = true;
    if (!one_time_day && !moment(exec_seconds, 'HH:mm:ss', true).isValid()) {
      setFormErrorState(prevState => ({
        ...prevState,
        exec_secondsError: true
      }));
      isValid = false;
    }

    if (one_time_day && !moment(exec_time, 'HH:mm:ss', true).isValid()) {
      setFormErrorState(prevState => ({
        ...prevState,
        exec_timeError: true
      }));
      isValid = false;
    }

    return isValid;
  }

  async function handleSubmit() {
    try {
      setFormErrorState(formErrorInitialState);

      await schema.validate(
        {
          name,
          description,
          login,
          password,
          ipv4_address,
          port
        },
        { abortEarly: false }
      );

      if (validateDates()) {
        if (edit) {
          const editLprConfigInput = {
            id: selected.id,
            exec_seconds,
            exec_time,
            name,
            description,
            login,
            password,
            ipv4_address,
            one_time_day,
            active,
            port
          };
          await updateSupplierConfig({
            variables: {
              editLprConfigInput
            }
          });
        } else {
          const addLprConfigInput = {
            name,
            exec_time,
            exec_seconds,
            description,
            login,
            password,
            ipv4_address,
            port,
            active,
            one_time_day
          };

          await addSupplier({
            variables: {
              addLprConfigInput
            }
          });
        }

        setFormState(formInitialState);

        let successMessage;

        if (edit) successMessage = 'El registro fue editado exitosamente';
        else successMessage = 'El registro fue creado exitosamente';

        toast.success(successMessage);

        handleClose();
      }
    } catch (err) {
      validateDates();
      if (err.inner) {
        err.inner.map(error =>
          setFormErrorState(prevState => ({
            ...prevState,
            [`${error.path}Error`]: true
          }))
        );
      } else {
        let errorMessage;

        if (edit) errorMessage = 'Error al intentar actualizar el registro';
        else errorMessage = 'Error al intentar crear el registro';

        toast.error(errorMessage);
      }
    }
  }

  function handleClose() {
    onClose(selectedValue);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        {edit ? 'Editar' : 'Ingresar'} Configuración de Servicio
      </DialogTitle>
      <Main data-testid="modal-component">
        <Form>
          <Form.Group>
            <Form.Input
              error={nameError}
              label="Nombre *"
              placeholder="Nombre"
              width={4}
              value={name}
              onChange={(e, { value }) => handleOnChange('name', value)}
              className="plateInput"
            />

            {one_time_day && (
              <Form.Input
                error={exec_timeError}
                label="Hora de ejecución *"
                width={4}
                value={exec_time}
                className="plateInput"
              >
                <MaskedInput
                  mask={timeMask}
                  placeholder="10:00:00"
                  value={exec_time}
                  onChange={e => handleOnChange('exec_time', e.target.value)}
                />
              </Form.Input>
            )}
            {!one_time_day && (
              <Form.Input
                error={exec_secondsError}
                label="Intervalo de segundos *"
                width={4}
                value={exec_seconds}
                className="plateInput"
              >
                <MaskedInput
                  mask={timeMask}
                  placeholder="00:00:50"
                  value={exec_seconds}
                  onChange={e => handleOnChange('exec_seconds', e.target.value)}
                />
              </Form.Input>
            )}
          </Form.Group>
          <Form.Group>
            <Form.TextArea
              error={descriptionError}
              label="Descripción"
              placeholder="Descripción"
              width={16}
              value={description}
              onChange={(e, { value }) => handleOnChange('description', value)}
              className="descriptionInput"
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              error={ipv4_addressError}
              label="IP Address"
              placeholder="IP Address"
              width={4}
              value={ipv4_address}
              onChange={(e, { value }) => handleOnChange('ipv4_address', value)}
              className="plateInput"
            />
            <Form.Input
              error={portError}
              label="Puerto"
              placeholder="Puerto "
              width={4}
              value={port}
              onChange={(e, { value }) => handleOnChange('port', value)}
              className="plateInput"
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              error={loginError}
              label="Login"
              placeholder="Login"
              width={4}
              value={login}
              onChange={(e, { value }) => handleOnChange('login', value)}
              className="plateInput"
            />
            <Form.Input
              error={passwordError}
              label="Password"
              placeholder="Password"
              width={4}
              value={password}
              onChange={(e, { value }) => handleOnChange('password', value)}
              className="plateInput"
              type="password"
            />
          </Form.Group>

          <Form.Group>
            <Form.Checkbox
              label="Activo"
              placeholder="Activo"
              width={2}
              onClick={() => handleOnChange('active', !active)}
              checked={active}
            />
            <Form.Checkbox
              label="Ejecución diaria"
              placeholder="Ejecución diaria"
              toggle
              width={4}
              onClick={() => handleOnChange('one_time_day', !one_time_day)}
              checked={one_time_day}
            />
          </Form.Group>
        </Form>
        <Button onClick={handleSubmit} data-testid="send-button">
          {addLoading || updateLoading ? (
            <Loading height={15} width={15} type="spin" />
          ) : (
            <Send>
              <MdSend />
              Enviar
            </Send>
          )}
        </Button>
      </Main>
    </Dialog>
  );
}

ServicesConfigurationModal.propTypes = {
  edit: PropTypes.bool,
  onClose: PropTypes.func,
  selectedValue: PropTypes.any,
  open: PropTypes.bool,
  selected: PropTypes.object,
  malls: PropTypes.array
};
